import { computed, defineComponent, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLocale } from "../../locales/i18n.js";
import {
    useFetch,
} from "../../utils.js";
import VoiceMenu from "./menus/voice.js";
import VoiceSelfMenu from "./menus/voiceSelf.js";
import OverviewMenu from "./menus/overview.js";
import {
    buildTimeRange,
    isTimeRangeKey,
    TIME_RANGE_OPTIONS,
    TimeRangeKey,
    TimeRangeParams,
} from "./timeRange.js";
import { MemberWithRoles } from "./types.js";
import { UserProfileCard } from "./profile/UserProfileCard.js";
import { UserProfileNav } from "./profile/UserProfileNav.js";
import { UserVoiceSubnav } from "./profile/UserVoiceSubnav.js";
import {
    isUserProfileVoiceMenu,
    resolveUserProfileMenu,
} from "./profile/profileHelpers.js";

//////////////////////////////////////////////////
//  COMPONENT
//////////////////////////////////////////////////

export default defineComponent({
    name: "UsersPage",

    setup() {
        const { i18n }   = useLocale();
        const { fetch }  = useFetch();
        const route      = useRoute();
        const router     = useRouter();
        const refUser    = ref<MemberWithRoles | null>(null);
        const refLoading = ref<boolean>(true);
        const refError   = ref<"not_found" | "load_failed" | null>(null);

        const initialRange = isTimeRangeKey(route.query["range"]) ? route.query["range"] as TimeRangeKey : "7d";
        const refRangeKey  = ref<TimeRangeKey>(initialRange);

        const timeRange = computed<TimeRangeParams>(() => buildTimeRange(refRangeKey.value));
        const currentMenu = computed(() => resolveUserProfileMenu(route.params["menu"] as string | undefined));
        const limit = computed<number | undefined>(() => {
            const raw = Number(route.query["limit"]);
            return Number.isFinite(raw) ? raw : undefined;
        });

        watch(() => route.query["range"], (value) => {
            if (isTimeRangeKey(value))
                refRangeKey.value = value;
        });

        watch(() => route.params["id"], async (userId) => {
            if (!userId)
                return;

            refLoading.value = true;
            refError.value   = null;
            refUser.value    = null;

            try {
                const response = await fetch(`/api/members/${userId}`);

                if (response.status === 404) {
                    refError.value = "not_found";
                    return;
                }

                if (!response.ok)
                    throw new Error(`Failed to load user profile: ${response.status}`);

                const user = await response.json() as MemberWithRoles;

                user.roles = Array.isArray(user.roles)
                    ? [ ...user.roles ].sort((a, b) => (b.position || 0) - (a.position || 0))
                    : [];

                refUser.value = user;

            } catch (err) {
                refError.value = "load_failed";

            } finally {
                refLoading.value = false;
            }
        }, { immediate: true });

        function setTimeRange(key: TimeRangeKey) {
            refRangeKey.value = key;
            router.replace({
                query: {
                    ...route.query,
                    range: key,
                },
            });
        }

        function renderTimeRangeSelector() {
            if (!isUserProfileVoiceMenu(currentMenu.value))
                return null;

            return (
                <div class="user-profile-period-picker">
                    {TIME_RANGE_OPTIONS.map((option) => (
                        <div
                            class={{
                                "user-profile-period-item": true,
                                active: refRangeKey.value === option.key,
                            }}
                            onClick={() => setTimeRange(option.key)}
                            key={option.key}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            );
        }

        function renderMenuContent() {
            if (!refUser.value)
                return null;

            switch (currentMenu.value) {
                case "voice":
                    return (
                        <VoiceMenu
                            userId={refUser.value.id}
                            timeRange={timeRange.value}
                            limit={limit.value ?? 50}
                        />
                    );

                case "voice_self":
                    return (
                        <VoiceSelfMenu
                            userId={refUser.value.id}
                            user={refUser.value}
                            timeRange={timeRange.value}
                            limit={limit.value ?? 99}
                        />
                    );

                default:
                    return (
                        <OverviewMenu
                            userId={refUser.value.id}
                            user={refUser.value}
                        />
                    );
            }
        }

        return () => {
            if (refLoading.value)
                return <div class="notice">{i18n("page.users.loading_text")}</div>;

            if (refError.value)
                return (
                    <div id="app">
                        <div class="user-profile-error-state">
                            <span class="material-symbols-outlined">
                                {refError.value === "not_found" ? "person_off" : "error"}
                            </span>
                            <h1>
                                {refError.value === "not_found"
                                    ? "Utilisateur introuvable"
                                    : "Impossible de charger le profil"}
                            </h1>
                            <p>
                                {refError.value === "not_found"
                                    ? "Cet utilisateur Discord n'existe pas ou n'est pas accessible depuis ce serveur."
                                    : "Une erreur est survenue pendant le chargement du profil utilisateur."}
                            </p>
                        </div>
                    </div>
                );

            if (!refUser.value)
                return null;

            const member = refUser.value;

            return (
                <div id="app">
                    <div class="user-profile-page">
                        <div class="user-profile-sidebar">
                            <UserProfileCard member={member} />
                        </div>

                        <main class="user-profile-main">
                            <UserProfileNav
                                userId={member.id}
                                activeMenu={currentMenu.value}
                            />

                            {isUserProfileVoiceMenu(currentMenu.value) && (
                                <div class="user-profile-voice-controls">
                                    <UserVoiceSubnav
                                        userId={member.id}
                                        activeMenu={currentMenu.value}
                                    />

                                    {renderTimeRangeSelector()}
                                </div>
                            )}

                            <div class="user-profile-content">
                                {renderMenuContent()}
                            </div>
                        </main>
                    </div>
                </div>
            );
        };
    },
});
